<template>
  <div class="report-page">
    <!-- 出库类型选择 -->
    <el-card class="mb-4" shadow="never">
      <div class="bar" style="justify-content: space-between;">
        <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
          <span class="bar-label">出库类型</span>
          <el-radio-group v-model="currentType" size="small" @change="onTypeChange">
            <el-radio-button v-for="t in types" :key="t" :label="t">{{ t }}</el-radio-button>
          </el-radio-group>
          <span v-if="!types.length" class="muted">当前账号没有任何出库类型权限</span>
        </div>
        <el-button
          v-if="currentType"
          type="success"
          size="small"
          icon="el-icon-download"
          style="flex-shrink: 0;"
          @click="exportCombinedReport"
        >
          导出报表
        </el-button>
      </div>
    </el-card>

    <template v-if="currentType">
      <!-- 时间与多条件筛选 -->
      <el-card class="mb-4" shadow="never">
        <!-- 第一行：统计周期独立一行 -->
        <div class="bar" style="margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px dashed #ebeef5;">
          <span class="bar-label">统计周期</span>
          <el-radio-group v-model="dateType" size="small" @change="onDateTypeChange">
            <el-radio-button label="day">日</el-radio-button>
            <el-radio-button label="month">月</el-radio-button>
            <el-radio-button label="quarter">季</el-radio-button>
            <el-radio-button label="year">年</el-radio-button>
            <el-radio-button label="all">总</el-radio-button>
          </el-radio-group>
          <template v-if="dateType === 'day'">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              size="small"
              unlink-panels
              style="width: 380px; flex-shrink: 0;"
              @change="fetchReport">
            </el-date-picker>
          </template>
          <template v-else-if="dateType === 'month'">
            <el-date-picker
              v-model="dateRange"
              type="monthrange"
              range-separator="至"
              start-placeholder="开始月份"
              end-placeholder="结束月份"
              value-format="yyyy-MM"
              size="small"
              unlink-panels
              style="width: 380px; flex-shrink: 0;"
              @change="fetchReport">
            </el-date-picker>
          </template>
          <template v-else-if="dateType === 'quarter'">
            <el-date-picker
              v-model="quarterYear"
              type="year"
              placeholder="年份"
              value-format="yyyy"
              size="small"
              :clearable="false"
              style="width: 120px; flex-shrink: 0;"
              @change="fetchReport">
            </el-date-picker>
            <el-select
              v-model="quarterValue"
              placeholder="季度"
              size="small"
              style="width: 170px; flex-shrink: 0; margin-left: 8px;"
              @change="fetchReport"
            >
              <el-option label="第一季度 (1月~3月)" :value="1"></el-option>
              <el-option label="第二季度 (4月~6月)" :value="2"></el-option>
              <el-option label="第三季度 (7月~9月)" :value="3"></el-option>
              <el-option label="第四季度 (10月~12月)" :value="4"></el-option>
            </el-select>
          </template>
          <template v-else-if="dateType === 'year'">
            <el-date-picker
              v-model="yearStart"
              type="year"
              placeholder="开始年份"
              value-format="yyyy"
              size="small"
              style="width: 120px; flex-shrink: 0;"
              @change="fetchReport">
            </el-date-picker>
            <span style="margin:0 4px;color:#606266;flex-shrink:0;">至</span>
            <el-date-picker
              v-model="yearEnd"
              type="year"
              placeholder="结束年份"
              value-format="yyyy"
              size="small"
              style="width: 120px; flex-shrink: 0;"
              @change="fetchReport">
            </el-date-picker>
          </template>
          <span v-else class="muted" style="flex-shrink: 0;">全部历史</span>
        </div>

        <!-- 第二行：客户姓名、开发人、客户负责人等详细搜索项换行独立展示 -->
        <div class="bar">
          <span class="bar-label">条件筛选</span>

          <!-- 独立搜索项：客户姓名（支持下拉联想选人）、开发人、客户负责人、产品名称 -->
          <el-autocomplete
            v-model="customerFilter"
            :fetch-suggestions="queryCustomerSearch"
            placeholder="客户、配偶姓名"
            prefix-icon="el-icon-user"
            size="small"
            clearable
            :trigger-on-focus="false"
            style="width: 180px; flex-shrink: 0;"
            popper-class="customer-autocomplete-popper"
            @select="handleCustomerSelect"
            @input="onFilterChange"
            @clear="onFilterChange"
            @keyup.enter.native="handleSearch"
          >
            <template slot-scope="{ item }">
              <div style="display: flex; align-items: center; justify-content: space-between; font-size: 13px; line-height: 1.5; padding: 2px 0;">
                <span style="font-weight: 500; color: #303133;">{{ item.name }}</span>
                <span v-if="item.phone" style="font-size: 12px; color: #909399; margin-left: 8px;"> | 手机：{{ item.phone }}</span>
              </div>
            </template>
          </el-autocomplete>

          <el-autocomplete
            v-model="developerFilter"
            :fetch-suggestions="queryDeveloperSearch"
            placeholder="开发人"
            prefix-icon="el-icon-s-custom"
            size="small"
            clearable
            :trigger-on-focus="false"
            style="width: 140px; flex-shrink: 0;"
            @select="onFilterChange"
            @input="onFilterChange"
            @clear="onFilterChange"
            @keyup.enter.native="handleSearch"
          >
          </el-autocomplete>

          <el-autocomplete
            v-model="managerFilter"
            :fetch-suggestions="queryManagerSearch"
            placeholder="客户负责人"
            prefix-icon="el-icon-s-check"
            size="small"
            clearable
            :trigger-on-focus="false"
            style="width: 140px; flex-shrink: 0;"
            @select="onFilterChange"
            @input="onFilterChange"
            @clear="onFilterChange"
            @keyup.enter.native="handleSearch"
          >
          </el-autocomplete>

          <el-input
            v-model="productFilter"
            placeholder="产品名称"
            prefix-icon="el-icon-goods"
            size="small"
            clearable
            style="width: 150px; flex-shrink: 0;"
            @input="onFilterChange"
            @clear="onFilterChange"
            @keyup.enter.native="handleSearch"
          >
          </el-input>

          <!-- 当出库类型为试用时，增加 试用类型 和 是否消费 筛选 -->
          <template v-if="currentType === '试用'">
            <el-select
              v-model="trialTypeFilter"
              placeholder="试用类型"
              size="small"
              clearable
              style="width: 130px; flex-shrink: 0;"
              @change="onTrialFilterChange"
            >
              <el-option v-for="t in applicableTypeOptions" :key="t.id" :label="t.name" :value="t.id"></el-option>
            </el-select>

            <el-select
              v-model="consumptionStatusFilter"
              placeholder="是否消费"
              size="small"
              clearable
              style="width: 110px; flex-shrink: 0;"
              @change="onTrialFilterChange"
            >
              <el-option label="已消费" :value="1"></el-option>
              <el-option label="未消费" :value="0"></el-option>
            </el-select>
          </template>

          <!-- 当出库类型为礼品时，增加 礼品类型 与 客户身份 筛选 -->
          <template v-if="currentType === '礼品'">
            <el-select
              v-model="giftTypeFilter"
              placeholder="礼品类型"
              size="small"
              clearable
              style="width: 130px; flex-shrink: 0;"
              @change="onGiftTypeFilterChange"
            >
              <el-option v-for="t in giftTypeOptions" :key="t.id" :label="t.name" :value="t.id"></el-option>
            </el-select>

            <el-select
              v-model="customerIdentityFilter"
              placeholder="客户身份"
              size="small"
              clearable
              style="width: 130px; flex-shrink: 0;"
              @change="onCustomerIdentityFilterChange"
            >
              <el-option
                v-for="t in giftIdentityOptions"
                :key="t.value"
                :label="t.label"
                :value="t.value"
              ></el-option>
            </el-select>
          </template>

          <!-- 当出库类型为送存货时，增加 存货类型 筛选 -->
          <template v-if="currentType === '送存货'">
            <el-select
              v-model="stockTypeFilter"
              placeholder="存货类型"
              size="small"
              clearable
              style="width: 130px; flex-shrink: 0;"
              @change="onStockTypeFilterChange"
            >
              <el-option label="普通存货" value="普通存货"></el-option>
              <el-option label="品鉴存货" value="品鉴存货"></el-option>
            </el-select>
          </template>

          <el-button
            type="primary"
            size="small"
            icon="el-icon-search"
            style="flex-shrink: 0;"
            @click="handleSearch"
          >
            搜索
          </el-button>
          <el-button
            size="small"
            icon="el-icon-refresh-left"
            style="flex-shrink: 0;"
            @click="resetFilters"
          >
            重置
          </el-button>
        </div>
      </el-card>

      <!-- 汇总卡片 -->
      <div class="stat-grid mb-4" v-loading="loading">
        <div class="stat-card today"><div class="stat-num">{{ summary.todayQuantity || 0 }}</div><div class="stat-label">今日（件）</div></div>
        <div class="stat-card month"><div class="stat-num">{{ summary.monthQuantity || 0 }}</div><div class="stat-label">本月（件）</div></div>
        <div class="stat-card quarter"><div class="stat-num">{{ summary.quarterQuantity || 0 }}</div><div class="stat-label">本季（件）</div></div>
        <div class="stat-card year"><div class="stat-num">{{ summary.yearQuantity || 0 }}</div><div class="stat-label">本年（件）</div></div>
        <div class="stat-card all"><div class="stat-num">{{ summary.allQuantity || 0 }}</div><div class="stat-label">累计（件）</div></div>
      </div>

      <!-- 当前周期：产品聚合 + 记录 -->
      <el-card class="mb-4" shadow="never">
        <div slot="header" class="card-head">
          <span><i class="el-icon-data-line"></i> 当前周期统计（{{ rangeLabel }}）</span>
          <div class="head-tools">
            <span class="head-sum" style="margin-right: 8px;">共 {{ displayTotalQuantity }} 件</span>
            <el-button type="success" size="mini" icon="el-icon-download" @click="exportProductStats">导出</el-button>
          </div>
        </div>
        <el-table :data="pagedShownItems" size="small" border stripe max-height="360"
          :empty-text="loading ? '加载中...' : '暂无数据'"
          :header-cell-style="{ background:'#f5f7fa', color:'#606266' }">
          <el-table-column type="index" label="序号" width="70" align="center" :index="idx => (page1 - 1) * size1 + idx + 1"></el-table-column>
          <el-table-column prop="productName" label="产品名称" min-width="200"></el-table-column>
          <el-table-column prop="quantity" label="数量（件）" width="160" align="center"></el-table-column>
        </el-table>
        <el-pagination
          @size-change="v => { size1 = v; page1 = 1 }"
          @current-change="v => page1 = v"
          :current-page="page1"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="size1"
          layout="total, sizes, prev, pager, next, jumper"
          :total="shownItems.length"
          style="margin-top:16px; text-align:right">
        </el-pagination>
      </el-card>

      <!-- 产品试用记录 (仅出库类型为试用时展示) -->
      <el-card v-if="currentType === '试用'" class="mb-4" shadow="never">
        <div slot="header" class="card-head">
          <div>
            <span><i class="el-icon-s-opportunity"></i> 产品试用记录</span>
            <span v-if="filteredTrialRecords.length !== trialRecords.length" class="muted" style="margin-left: 8px; font-weight: normal; font-size: 13px;">(已筛选出 {{ filteredTrialRecords.length }} 条)</span>
          </div>
          <div class="head-tools">
            <el-button type="success" size="mini" icon="el-icon-download" @click="exportTrialRecords" :disabled="!filteredTrialRecords.length">导出试用记录</el-button>
          </div>
        </div>
        <el-table :data="pagedFilteredTrialRecords" size="small" stripe border max-height="420" style="width:100%"
          :empty-text="loading ? '加载中...' : '暂无产品试用记录'"
          :header-cell-style="{background:'#f5f7fa', color:'#606266'}">
          <el-table-column type="index" label="序号" width="60" align="center" :index="idx => (page3 - 1) * size3 + idx + 1"></el-table-column>
          <el-table-column prop="customerName" label="客户" min-width="100" align="center">
            <template slot-scope="s">
              <span style="font-weight: 500;">{{ s.row.customerName || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="productName" label="产品" min-width="200" align="center">
            <template slot-scope="s">
              <div style="font-weight:600;font-size:13px;line-height:1.4;">{{ s.row.productName || '-' }}</div>
              <div style="font-size:12px;color:#606266;margin-top:2px;">
                共 <strong style="color:#303133;">{{ s.row.totalQuantity !== undefined ? s.row.totalQuantity : (s.row.quantity || 0) }}</strong> 件
                <span style="font-size:11px;color:#909399;margin-left:2px;">(已审 {{ s.row.approvedQuantity !== undefined ? s.row.approvedQuantity : s.row.quantity }} · 待审 {{ s.row.pendingQuantity || 0 }})</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="试用类型" min-width="110" align="center">
            <template slot-scope="s">
              <el-tag v-if="s.row.applicableTypeName" :color="s.row.applicableTypeColor || ''" :style="s.row.applicableTypeColor ? 'color:#fff;border-color:' + s.row.applicableTypeColor : ''" size="mini">
                {{ s.row.applicableTypeName }}
              </el-tag>
              <span v-else style="color:#909399">-</span>
            </template>
          </el-table-column>
          <el-table-column label="试用次数" min-width="160" align="center">
            <template slot-scope="s">
              <div>
                <span style="color:#409EFF;font-weight:600;font-size:13px;">已申请 {{ s.row.totalApplyCount !== undefined ? s.row.totalApplyCount : ((s.row.approvedCount || 0) + (s.row.pendingCount || 0)) }} 次</span>
                <span v-if="s.row.applicableCount" style="color:#909399;font-size:11px;margin-left:4px">/ 限{{ s.row.maxAllowed || s.row.applicableCount }}次</span>
              </div>
              <div style="font-size:11px;color:#606266;margin-top:2px;">
                <span>已审 <strong style="color:#67C23A;">{{ s.row.approvedCount || 0 }} 次</strong></span>
                <span style="margin: 0 4px; color: #dcdfe6;">|</span>
                <span>待审 <strong :style="(s.row.pendingCount || 0) > 0 ? 'color:#E6A23C;' : 'color:#909399;'">{{ s.row.pendingCount || 0 }} 次</strong></span>
              </div>
              <div v-if="s.row.limitExceeded && s.row.isUnlocked" style="display:inline-flex;align-items:center;color:#67C23A;background:#f0f9eb;border:1px solid #c2e7b0;border-radius:4px;padding:1px 6px;font-size:11px;font-weight:600;margin-top:3px;">
                <i class="el-icon-unlock" style="margin-right:2px;font-weight:bold;"></i>已解限(可再提1次)
              </div>
              <div v-else-if="s.row.limitExceeded" style="display:inline-flex;align-items:center;color:#F56C6C;background:#fef0f0;border:1px solid #fde2e2;border-radius:4px;padding:1px 6px;font-size:11px;font-weight:600;margin-top:3px;">
                <i class="el-icon-warning-outline" style="margin-right:2px;"></i>已达上限
              </div>
              <div v-else-if="s.row.isUnlocked" style="display:inline-flex;align-items:center;color:#67C23A;background:#f0f9eb;border:1px solid #c2e7b0;border-radius:4px;padding:1px 6px;font-size:11px;font-weight:600;margin-top:3px;">
                <i class="el-icon-unlock" style="margin-right:2px;font-weight:bold;"></i>已特批开放
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="salesman" label="业务员" min-width="110" align="center">
            <template slot-scope="s">{{ s.row.salesman || '-' }}</template>
          </el-table-column>
          <el-table-column label="消费情况" width="100" align="center">
            <template slot-scope="s">
              <el-tag size="small" :type="s.row.consumptionStatus === 1 ? 'success' : 'info'" effect="plain">
                {{ s.row.consumptionStatus === 1 ? '已消费' : '未消费' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="消费时间" min-width="120" align="center">
            <template slot-scope="s">
              <span v-if="s.row.consumptionStatus === 1 && s.row.consumptionTime" style="color: #606266; font-size: 13px;">
                {{ (s.row.consumptionTime || '').toString().slice(0, 10) }}
              </span>
              <span v-else style="color: #909399;">-</span>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          @size-change="v => { size3 = v; page3 = 1 }"
          @current-change="v => page3 = v"
          :current-page="page3"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="size3"
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredTrialRecords.length"
          style="margin-top:16px; text-align:right">
        </el-pagination>
      </el-card>

      <!-- 明细记录 -->
      <el-card shadow="never">
        <div slot="header" class="card-head">
          <div>
            <span><i class="el-icon-tickets"></i> 出库记录</span>
            <span v-if="filteredRecords.length !== records.length" class="muted" style="margin-left: 8px; font-weight: normal; font-size: 13px;">(已筛选出 {{ filteredRecords.length }} 条，共 {{ filteredTotalQuantity }} 件)</span>
          </div>
          <div class="head-tools">
            <el-select v-model="statusFilter" size="mini" clearable placeholder="全部状态" style="width: 130px" @change="onFilterChange">
              <el-option v-for="o in recordStatusOptions" :key="o.value" :label="o.label" :value="o.value"></el-option>
            </el-select>
            <el-button type="success" size="mini" icon="el-icon-download" @click="exportRecords">导出</el-button>
          </div>
        </div>
        <el-table :data="pagedFilteredRecords" size="small" border stripe max-height="420"
          :header-cell-style="{ background:'#f5f7fa', color:'#606266' }">
          <el-table-column label="出库类型" min-width="110" align="center" show-overflow-tooltip>
            <template slot-scope="s">
              {{ formatRecordType(s.row) }}
            </template>
          </el-table-column>
          <el-table-column prop="productNames" label="产品" min-width="150" show-overflow-tooltip></el-table-column>
          <el-table-column prop="totalQuantity" label="件数" width="70" align="center"></el-table-column>
          <el-table-column prop="customerName" label="客户" min-width="90" align="center"></el-table-column>
          <el-table-column v-if="currentType === '礼品'" label="客户身份" min-width="110" align="center" show-overflow-tooltip>
            <template slot-scope="s">
              <el-tag
                v-if="s.row.customerIdentity || resolveCustomerIdentity(s.row)"
                size="mini"
                :color="s.row.customerIdentityColor || getCustomerIdentityColor(s.row) || ''"
                :style="(s.row.customerIdentityColor || getCustomerIdentityColor(s.row)) ? 'color:#fff;border-color:' + (s.row.customerIdentityColor || getCustomerIdentityColor(s.row)) : ''"
              >
                {{ s.row.customerIdentity || resolveCustomerIdentity(s.row) }}
              </el-tag>
              <span v-else style="color: #909399;">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="developer" label="开发人" min-width="100" align="center" show-overflow-tooltip>
            <template slot-scope="s">{{ s.row.developer || '-' }}</template>
          </el-table-column>
          <el-table-column prop="customerManager" label="客户负责人" min-width="100" align="center" show-overflow-tooltip>
            <template slot-scope="s">{{ s.row.customerManager || '-' }}</template>
          </el-table-column>
          <el-table-column label="生产批次" min-width="110" align="center" show-overflow-tooltip>
            <template slot-scope="s">{{ s.row.batchNos || '-' }}</template>
          </el-table-column>
          <el-table-column label="经办人" width="90" align="center">
            <template slot-scope="s">{{ s.row.operator || '-' }}</template>
          </el-table-column>
          <el-table-column label="库管" width="90" align="center">
            <template slot-scope="s">{{ s.row.warehouseKeeper || '-' }}</template>
          </el-table-column>
          <el-table-column label="状态" width="90" align="center">
            <template slot-scope="s">{{ statusText(s.row.status) }}</template>
          </el-table-column>
          <el-table-column v-if="currentType === '试用'" label="是否消费" width="90" align="center">
            <template slot-scope="s">
              <el-tag size="mini" :type="s.row.consumptionStatus === 1 ? 'success' : 'info'">{{ s.row.consumptionStatus === 1 ? '已消费' : '未消费' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column v-if="currentType === '试用'" label="消费时间" width="120" align="center">
            <template slot-scope="s">{{ (s.row.consumptionTime || '').toString().slice(0, 10) || '-' }}</template>
          </el-table-column>
          <el-table-column label="出库时间" width="120" align="center">
            <template slot-scope="s">{{ [0, 2, 6].includes(Number(s.row.status)) ? '-' : ((s.row.finishTime || '').toString().slice(0,10) || '-') }}</template>
          </el-table-column>
        </el-table>
        <el-pagination
          @size-change="v => { size2 = v; page2 = 1 }"
          @current-change="v => page2 = v"
          :current-page="page2"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="size2"
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredRecords.length"
          style="margin-top:16px; text-align:right">
        </el-pagination>
        <div v-if="!records.length && !loading" class="muted empty">该周期暂无出库记录</div>
        <div v-else-if="!filteredRecords.length && !loading" class="muted empty">未找到符合筛选条件的出库记录</div>
      </el-card>
    </template>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { reportApi, customerApi, giftTypeApi } from '../api/api'

const ALL_TYPES = ['礼品', '试用', '新出库', '送存货', '尊贵卡A', '尊贵卡B', '尊贵卡C']
const STATUS_TEXT = { 0: '审核中', 1: '待签字', 2: '已驳回', 3: '已完结', 4: '未完结', 5: '待出库', 6: '已撤销', '-1': '已驳回' }

function pad (n) { return String(n).padStart(2, '0') }

export default {
  name: 'Report',
  data () {
    return {
      user: {},
      types: [],
      currentType: '',
      dateType: 'day',
      dateRange: null,
      quarterYear: String(new Date().getFullYear()),
      quarterValue: Math.floor(new Date().getMonth() / 3) + 1,
      yearStart: '',
      yearEnd: '',
      customerFilter: '',
      customerList: [],
      developerFilter: '',
      managerFilter: '',
      productFilter: '',
      giftTypeFilter: '',
      giftTypeOptions: [],
      customerIdentityFilter: '',
      trialTypeFilter: '',
      stockTypeFilter: '',
      consumptionStatusFilter: '',
      applicableTypeOptions: [],
      loading: false,
      summary: {},
      items: [],
      records: [],
      trialRecords: [],
      totalCount: 0,
      totalQuantity: 0,
      statusFilter: '',
      page1: 1,
      size1: 20,
      page2: 1,
      size2: 20,
      page3: 1,
      size3: 10,
      // 报表已排除已驳回/已撤销，记录里只会出现这几种状态
      recordStatusOptions: [
        { value: 0, label: '审核中' },
        { value: 1, label: '待签字' },
        { value: 4, label: '未完结' },
        { value: 3, label: '已完结' }
      ]
    }
  },
  computed: {
    rangeLabel () {
      if (this.dateType === 'all') return '全部'
      if (this.dateType === 'quarter') {
        const y = Number(this.quarterYear || new Date().getFullYear())
        const q = Number(this.quarterValue || (Math.floor(new Date().getMonth() / 3) + 1))
        const startMonth = (q - 1) * 3 + 1
        const endMonth = q * 3
        const lastDay = new Date(y, endMonth, 0).getDate()
        return `${y}年第${q}季度（${y}-${pad(startMonth)}-01 至 ${y}-${pad(endMonth)}-${pad(lastDay)}）`
      }
      if (this.dateType === 'year') {
        if (this.yearStart && this.yearEnd) return this.yearStart + ' 至 ' + this.yearEnd
        return this.yearStart || this.yearEnd || '全部'
      }
      if (this.dateRange && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
        return this.dateRange[0] + ' 至 ' + this.dateRange[1]
      }
      return '全部'
    },
    // 当前周期统计：根据筛选条件动态收窄或聚合各产品数量
    shownItems () {
      const pFilter = (this.productFilter || '').trim().toLowerCase()
      // 当有搜索条件（客户、开发人、客户负责人、状态等）时，从筛选后的出库明细中动态聚合产品及其数量
      if (this.hasActiveFilter) {
        const aggMap = {}
        for (const r of this.filteredRecords) {
          if (Array.isArray(r.items) && r.items.length) {
            for (const it of r.items) {
              const pName = it.productName || '未知产品'
              aggMap[pName] = (aggMap[pName] || 0) + (Number(it.quantity) || 0)
            }
          } else {
            const pName = r.productNames || '未知产品'
            aggMap[pName] = (aggMap[pName] || 0) + (Number(r.totalQuantity) || 0)
          }
        }
        let list = Object.keys(aggMap).map(k => ({ productName: k, quantity: aggMap[k] }))
        if (pFilter) {
          list = list.filter(i => String(i.productName || '').toLowerCase().includes(pFilter))
        }
        return list.filter(i => Number(i.quantity) > 0)
      }

      let baseItems = this.items || []
      if (!(this.currentType === '礼品' && this.giftTypeFilter)) {
        baseItems = baseItems.filter(i => Number(i.quantity) > 0)
      }
      if (pFilter) {
        baseItems = baseItems.filter(i => String(i.productName || '').toLowerCase().includes(pFilter))
      }
      return baseItems
    },
    pagedShownItems () {
      const start = (this.page1 - 1) * this.size1
      const end = start + this.size1
      return this.shownItems.slice(start, end)
    },
    giftIdentityOptions () {
      const map = new Map()
      ;(this.giftTypeOptions || []).forEach(t => {
        const val = (t.customerCategoryName && t.customerCategoryName.trim()) || (t.name ? t.name.replace(/礼品/g, '客户') : '')
        if (val && !map.has(val)) {
          map.set(val, { label: val, value: val, color: t.color })
        }
      })
      ;(this.records || []).forEach(r => {
        const iden = r.customerIdentity || this.resolveCustomerIdentity(r)
        if (iden && !map.has(iden)) {
          map.set(iden, { label: iden, value: iden, color: r.customerIdentityColor || this.getCustomerIdentityColor(r) })
        }
      })
      return Array.from(map.values())
    },
    // 出库记录按状态、客户姓名（单人/合称）、开发人、客户负责人、产品名称多重精准筛选
    filteredRecords () {
      let list = this.records || []
      if (this.statusFilter !== '' && this.statusFilter != null) {
        list = list.filter(r => Number(r.status) === Number(this.statusFilter))
      }
      if (this.currentType === '礼品' && this.customerIdentityFilter) {
        const idFilter = this.customerIdentityFilter.trim().toLowerCase()
        list = list.filter(r => {
          const ident = String(r.customerIdentity || this.resolveCustomerIdentity(r) || '').toLowerCase()
          return ident.includes(idFilter)
        })
      }
      const cFilter = (this.customerFilter || '').trim().toLowerCase()
      if (cFilter) {
        list = list.filter(r => {
          const cName = String(r.customerName || '').toLowerCase().trim()
          const sName = String(r.spouseName || '').toLowerCase().trim()
          if (!cName && !sName) return false
          return cName.includes(cFilter) || (sName && sName.includes(cFilter))
        })
      }
      const devFilter = (this.developerFilter || '').trim().toLowerCase()
      if (devFilter) {
        list = list.filter(r => {
          const dev = String(r.developer || '').toLowerCase().trim()
          return dev && dev.includes(devFilter)
        })
      }
      const mgrFilter = (this.managerFilter || '').trim().toLowerCase()
      if (mgrFilter) {
        const matchingCustomerNames = new Set(
          (this.customerList || [])
            .filter(c => (c.responsiblePerson && c.responsiblePerson.toLowerCase().includes(mgrFilter)))
            .map(c => c.name.toLowerCase().trim())
        )
        list = list.filter(r => {
          const mgr = String(r.customerManager || '').toLowerCase().trim()
          if (mgr && mgr.includes(mgrFilter)) return true
          const cName = String(r.customerName || '').toLowerCase().trim()
          return cName && matchingCustomerNames.has(cName)
        })
      }
      const pFilter = (this.productFilter || '').trim().toLowerCase()
      if (pFilter) {
        list = list.filter(r => String(r.productNames || '').toLowerCase().includes(pFilter))
      }
      return list
    },
    // 产品试用记录按客户姓名、开发人、客户负责人、产品名称筛选
    filteredTrialRecords () {
      let list = this.trialRecords || []
      const cFilter = (this.customerFilter || '').trim().toLowerCase()
      if (cFilter) {
        list = list.filter(r => {
          const cName = String(r.customerName || '').toLowerCase().trim()
          const sName = String(r.spouseName || '').toLowerCase().trim()
          if (!cName && !sName) return false
          return cName.includes(cFilter) || (sName && sName.includes(cFilter))
        })
      }
      const devFilter = (this.developerFilter || '').trim().toLowerCase()
      if (devFilter) {
        list = list.filter(r => {
          const dev = String(r.developer || '').toLowerCase().trim()
          return dev && dev.includes(devFilter)
        })
      }
      const mgrFilter = (this.managerFilter || '').trim().toLowerCase()
      if (mgrFilter) {
        const matchingCustomerNames = new Set(
          (this.customerList || [])
            .filter(c => (c.responsiblePerson && c.responsiblePerson.toLowerCase().includes(mgrFilter)))
            .map(c => c.name.toLowerCase().trim())
        )
        list = list.filter(r => {
          const mgr = String(r.customerManager || '').toLowerCase().trim()
          if (mgr && mgr.includes(mgrFilter)) return true
          const cName = String(r.customerName || '').toLowerCase().trim()
          return cName && matchingCustomerNames.has(cName)
        })
      }
      const pFilter = (this.productFilter || '').trim().toLowerCase()
      if (pFilter) {
        list = list.filter(r => String(r.productName || '').toLowerCase().includes(pFilter))
      }
      return list
    },
    pagedFilteredTrialRecords () {
      const start = (this.page3 - 1) * this.size3
      const end = start + this.size3
      return this.filteredTrialRecords.slice(start, end)
    },
    filteredTotalQuantity () {
      return this.filteredRecords.reduce((sum, r) => sum + (Number(r.totalQuantity) || 0), 0)
    },
    hasActiveFilter () {
      return !!((this.customerFilter && this.customerFilter.trim()) ||
                (this.developerFilter && this.developerFilter.trim()) ||
                (this.managerFilter && this.managerFilter.trim()) ||
                (this.productFilter && this.productFilter.trim()) ||
                (this.statusFilter !== '' && this.statusFilter != null) ||
                (this.currentType === '礼品' && this.customerIdentityFilter))
    },
    displayTotalQuantity () {
      return this.hasActiveFilter ? this.filteredTotalQuantity : this.totalQuantity
    },
    pagedFilteredRecords () {
      const start = (this.page2 - 1) * this.size2
      const end = start + this.size2
      return this.filteredRecords.slice(start, end)
    }
  },
  created () {
    try { this.user = JSON.parse(sessionStorage.getItem('user') || '{}') } catch (e) { this.user = {} }
    this.dateRange = null
    this.yearStart = ''
    this.yearEnd = ''

    // 支持从外部（如礼品名单）携带参数跳转
    if (this.$route.query.customer) {
      this.customerFilter = String(this.$route.query.customer).trim()
    }
    if (this.$route.query.developer) {
      this.developerFilter = String(this.$route.query.developer).trim()
    }
    if (this.$route.query.manager) {
      this.managerFilter = String(this.$route.query.manager).trim()
    }
    if (this.$route.query.product) {
      this.productFilter = String(this.$route.query.product).trim()
    }
    if (this.$route.query.dateType) {
      this.dateType = this.$route.query.dateType
    }
    if (this.$route.query.quarter) {
      this.quarterValue = Number(this.$route.query.quarter)
    }
    if (this.$route.query.quarterYear) {
      this.quarterYear = String(this.$route.query.quarterYear)
    }
    if (this.$route.query.giftType || this.$route.query.giftTypeId) {
      this.giftTypeFilter = Number(this.$route.query.giftTypeId || this.$route.query.giftType) || this.$route.query.giftType
    }
    if (this.$route.query.customerIdentity) {
      this.customerIdentityFilter = String(this.$route.query.customerIdentity).trim()
    }
    this.loadTypes()
    this.loadApplicableTypes()
    this.loadGiftTypes()
    this.loadCustomerList()
  },
  watch: {
    '$route' () {
      this.handleRouteQuery()
    }
  },
  methods: {
    loadCustomerList () {
      customerApi.getCustomerList(this.scopeParams()).then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        const isValidPhone = p => {
          const s = String(p || '').trim()
          return s && s !== '00000000000' && !/^0+$/.test(s) ? s : ''
        }
        this.customerList = list.map(c => ({
          value: c.name || '',
          name: c.name || '',
          developer: c.developer || '',
          responsiblePerson: c.responsiblePerson || '',
          phone: isValidPhone(c.contactPhone) || isValidPhone(c.phone) || isValidPhone(c.telephone) || ''
        })).filter(c => c.name)
      }).catch(() => {
        this.customerList = []
      })
    },
    queryCustomerSearch (queryString, cb) {
      if (!queryString || !queryString.trim()) {
        cb([])
        return
      }
      const q = queryString.trim().toLowerCase()
      const map = new Map()
      ;(this.customerList || []).forEach(c => {
        if (c.name && !map.has(c.name)) {
          map.set(c.name, {
            value: c.name,
            name: c.name,
            phone: c.phone || ''
          })
        }
      })
      ;(this.records || []).forEach(r => {
        if (r.customerName && !map.has(r.customerName)) {
          map.set(r.customerName, {
            value: r.customerName,
            name: r.customerName,
            phone: r.customerPhone || r.phone || ''
          })
        }
      })
      const source = Array.from(map.values())
      const matched = source.filter(c => {
        const nameMatch = c.name && c.name.toLowerCase().includes(q)
        const phoneMatch = c.phone && c.phone.includes(q)
        return nameMatch || phoneMatch
      })

      cb(matched)
    },
    queryDeveloperSearch (queryString, cb) {
      const q = (queryString || '').trim().toLowerCase()
      const set = new Set()
      ;(this.customerList || []).forEach(c => {
        if (c.developer && c.developer.trim()) set.add(c.developer.trim())
      })
      ;(this.records || []).forEach(r => {
        if (r.developer && r.developer.trim()) set.add(r.developer.trim())
      })
      const list = Array.from(set).map(d => ({ value: d, name: d }))
      const matched = q ? list.filter(item => item.name.toLowerCase().includes(q)) : list
      cb(matched)
    },
    queryManagerSearch (queryString, cb) {
      const q = (queryString || '').trim().toLowerCase()
      const set = new Set()
      ;(this.customerList || []).forEach(c => {
        if (c.responsiblePerson && c.responsiblePerson.trim()) set.add(c.responsiblePerson.trim())
      })
      ;(this.records || []).forEach(r => {
        if (r.customerManager && r.customerManager.trim()) set.add(r.customerManager.trim())
      })
      const list = Array.from(set).map(m => ({ value: m, name: m }))
      const matched = q ? list.filter(item => item.name.toLowerCase().includes(q)) : list
      cb(matched)
    },
    handleCustomerSelect (item) {
      this.customerFilter = item.name || item.value || ''
      this.onFilterChange()
    },
    handleSearch () {
      this.page1 = 1
      this.page2 = 1
      this.page3 = 1
      this.$nextTick(() => {
        if (this.$forceUpdate) this.$forceUpdate()
      })
    },
    onTypeChange () {
      this.giftTypeFilter = ''
      this.customerIdentityFilter = ''
      this.stockTypeFilter = ''
      this.trialTypeFilter = ''
      this.consumptionStatusFilter = ''
      this.onFilterChange()
      this.fetchReport()
    },
    onGiftTypeFilterChange () {
      this.onFilterChange()
      this.fetchReport()
    },
    onCustomerIdentityFilterChange () {
      this.onFilterChange()
      this.fetchReport()
    },
    resolveCustomerIdentity (r) {
      if (!r) return ''
      if (r.customerIdentity) return r.customerIdentity
      if (this.isGiftType(r)) {
        if (r.applicableTypeId && this.giftTypeOptions) {
          const opt = this.giftTypeOptions.find(t => t.id === r.applicableTypeId)
          if (opt) {
            return opt.customerCategoryName || (opt.name ? opt.name.replace(/礼品/g, '客户') : '')
          }
        }
        if (r.applicableTypeName) {
          const opt = (this.giftTypeOptions || []).find(t => t.name === r.applicableTypeName)
          if (opt) {
            return opt.customerCategoryName || (opt.name ? opt.name.replace(/礼品/g, '客户') : '')
          }
          return r.applicableTypeName.replace(/礼品/g, '客户')
        }
      }
      return ''
    },
    getCustomerIdentityColor (r) {
      if (!r) return ''
      if (r.customerIdentityColor) return r.customerIdentityColor
      if (this.isGiftType(r)) {
        const id = r.applicableTypeId
        const name = r.applicableTypeName
        const opt = (this.giftTypeOptions || []).find(t => (id && t.id === id) || (name && t.name === name))
        if (opt && opt.color) return opt.color
      }
      return ''
    },
    onTrialFilterChange () {
      this.onFilterChange()
      this.fetchReport()
    },
    loadApplicableTypes () {
      import('axios').then(({ default: axios }) => {
        axios.get('/applicable-type/list').then(res => {
          const list = Array.isArray(res.data) ? res.data : []
          this.applicableTypeOptions = list.filter(t => t.enabled !== 0)
        }).catch(() => { this.applicableTypeOptions = [] })
      })
    },
    loadGiftTypes () {
      giftTypeApi.getGiftTypeList(1).then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        this.giftTypeOptions = list.filter(t => t.enabled !== 0)
      }).catch(() => { this.giftTypeOptions = [] })
    },
    resetFilters () {
      this.customerFilter = ''
      this.developerFilter = ''
      this.managerFilter = ''
      this.productFilter = ''
      this.statusFilter = ''
      this.giftTypeFilter = ''
      this.customerIdentityFilter = ''
      this.trialTypeFilter = ''
      this.stockTypeFilter = ''
      this.consumptionStatusFilter = ''
      this.onFilterChange()
      this.fetchReport()
    },
    onStockTypeFilterChange () {
      this.onFilterChange()
      this.fetchReport()
    },
    handleRouteQuery () {
      this.customerFilter = String(this.$route.query.customer || '').trim()
      this.developerFilter = String(this.$route.query.developer || '').trim()
      this.managerFilter = String(this.$route.query.manager || '').trim()
      this.productFilter = String(this.$route.query.product || '').trim()
      this.dateType = this.$route.query.dateType || this.dateType || 'day'
      if (this.$route.query.quarter) {
        this.quarterValue = Number(this.$route.query.quarter)
      }
      if (this.$route.query.quarterYear) {
        this.quarterYear = String(this.$route.query.quarterYear)
      }
      const targetType = this.$route.query.type || this.$route.query.outboundType
      if (targetType) {
        this.currentType = targetType
      }
      if (this.$route.query.giftType || this.$route.query.giftTypeId) {
        this.giftTypeFilter = Number(this.$route.query.giftTypeId || this.$route.query.giftType) || this.$route.query.giftType
      }
      if (this.$route.query.customerIdentity) {
        this.customerIdentityFilter = String(this.$route.query.customerIdentity).trim()
      }
      this.onFilterChange()
      this.fetchReport()
    },
    statusText (s) { return STATUS_TEXT[s] != null ? STATUS_TEXT[s] : '其他' },
    formatRecordType (r) {
      if (!r) return '-'
      if (r.outboundType === '送存货') {
        return r.stockType || '普通存货'
      }
      if (r.outboundType === '试用' || r.outboundType === '产品试用') {
        return r.applicableTypeName || '试用'
      }
      if (this.isGiftType(r)) {
        const sub = (r.applicableTypeName && String(r.applicableTypeName).trim()) || ''
        const type = (r.outboundType && String(r.outboundType).trim()) || '礼品'
        if (type === '礼品') {
          return sub || '礼品'
        }
        return sub ? `${sub}·${type}` : type
      }
      return r.outboundType || '-'
    },
    isGiftType (r) {
      if (!r) return false
      const type = String(r.outboundType || '').trim()
      if (type === '礼品' || type.startsWith('礼品·')) return true
      if (['损耗', '试喝', '赠送', '过期'].includes(type)) return true
      if (r.applicableTypeId || r.applicableTypeName) {
        if (this.giftTypeOptions && this.giftTypeOptions.some(g => g.id === r.applicableTypeId || g.name === r.applicableTypeName)) {
          return true
        }
      }
      return false
    },
    onFilterChange () {
      this.page1 = 1
      this.page2 = 1
      this.page3 = 1
    },
    // 数据范围：超管全量(不传)、行政按 mineForUserId、业务员按 salesmanUserId
    scopeParams () {
      const u = this.user || {}
      if (u.role === 'admin') return { mineForUserId: u.id }
      if (u.role === 'salesman') return { salesmanUserId: u.id }
      return {} // super_admin → 全系统
    },
    loadTypes () {
      if (this.user.role === 'super_admin') {
        this.types = ALL_TYPES.slice()
        this.pickFirst()
        return
      }
      const name = this.user.name || this.user.username
      if (!name) { this.types = []; return }
      reportApi.getOutboundTypesByName(name).then(res => {
        const mine = Array.isArray(res.data) ? res.data : []
        this.types = ALL_TYPES.filter(t => mine.includes(t))
        this.pickFirst()
      }).catch(() => { this.types = [] })
    },
    pickFirst () {
      if (this.types.length) {
        const targetType = this.$route.query.type || this.$route.query.outboundType
        if (targetType && this.types.includes(targetType)) {
          this.currentType = targetType
        } else {
          this.currentType = this.types[0]
        }
        this.fetchReport()
      }
    },
    onDateTypeChange () {
      this.dateRange = null
      this.yearStart = ''
      this.yearEnd = ''
      if (this.dateType === 'quarter') {
        if (!this.quarterYear) this.quarterYear = String(new Date().getFullYear())
        if (!this.quarterValue) this.quarterValue = Math.floor(new Date().getMonth() / 3) + 1
      }
      this.fetchReport()
    },
    fetchReport () {
      if (!this.currentType || this.loading) return
      const params = Object.assign({
        outboundType: this.currentType,
        dateType: 'all'
      }, this.scopeParams())
      if (this.currentType === '礼品') {
        if (this.giftTypeFilter !== '' && this.giftTypeFilter != null) {
          params.applicableTypeId = this.giftTypeFilter
        }
        if (this.customerIdentityFilter) {
          params.customerIdentity = this.customerIdentityFilter
        }
      }
      if (this.currentType === '试用') {
        if (this.trialTypeFilter !== '' && this.trialTypeFilter != null) {
          params.applicableTypeId = this.trialTypeFilter
        }
        if (this.consumptionStatusFilter !== '' && this.consumptionStatusFilter != null) {
          params.consumptionStatus = this.consumptionStatusFilter
        }
      }
      if (this.currentType === '送存货') {
        if (this.stockTypeFilter) {
          params.stockType = this.stockTypeFilter
        }
      }
      if (this.dateType === 'quarter') {
        const y = Number(this.quarterYear || new Date().getFullYear())
        const q = Number(this.quarterValue || Math.floor(new Date().getMonth() / 3) + 1)
        const startMonth = (q - 1) * 3 + 1
        const endMonth = q * 3
        const lastDay = new Date(y, endMonth, 0).getDate()
        params.startDate = `${y}-${pad(startMonth)}-01`
        params.endDate = `${y}-${pad(endMonth)}-${pad(lastDay)}`
      } else if (this.dateType === 'year' && this.yearStart && this.yearEnd) {
        params.startDate = this.yearStart + '-01-01'
        params.endDate = this.yearEnd + '-12-31'
      } else if (this.dateType === 'month' && this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0] + '-01'
        const [y, m] = this.dateRange[1].split('-').map(Number)
        const last = new Date(y, m, 0)
        params.startDate = this.dateRange[0] + '-01'
        params.endDate = `${y}-${pad(m)}-${pad(last.getDate())}`
      } else if (this.dateType === 'day' && this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      this.loading = true
      reportApi.getOutboundReport(params).then(res => {
        const d = res.data || {}
        this.summary = d.statSummary || {}
        this.items = d.items || []
        this.records = d.list || []
        this.trialRecords = d.trialRecords || []
        this.totalCount = d.totalCount || 0
        this.totalQuantity = d.totalQuantity || 0
      }).catch(() => {
        this.summary = {}; this.items = []; this.records = []; this.trialRecords = []; this.totalCount = 0; this.totalQuantity = 0
      }).finally(() => { this.loading = false })
    },
    // 顶部总导出：导出包含 Sheet1(产品统计) 和 Sheet2(出库明细) 的单个 Excel 报表
    exportCombinedReport () {
      if (!this.shownItems.length && !this.filteredRecords.length && !this.filteredTrialRecords.length) {
        this.$message.warning('当前没有可导出的报表数据')
        return
      }
      try {
        const wb = XLSX.utils.book_new()
        const isTrial = this.currentType === '试用'
        const isGift = this.currentType === '礼品'

        // 1. Sheet 1: 产品统计
        const sheet1Data = [
          ['序号', '出库类型', '统计周期', '产品名称', '数量（件）']
        ]
        this.shownItems.forEach((item, idx) => {
          sheet1Data.push([
            idx + 1,
            this.currentType || '',
            this.rangeLabel || '全部',
            item.productName || '',
            Number(item.quantity) || 0
          ])
        })
        const totalSum = this.shownItems.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0)
        sheet1Data.push(['合计', this.currentType || '', this.rangeLabel || '全部', '全部产品', totalSum])
        const ws1 = XLSX.utils.aoa_to_sheet(sheet1Data)
        ws1['!cols'] = [
          { wch: 8 },
          { wch: 14 },
          { wch: 26 },
          { wch: 24 },
          { wch: 14 }
        ]
        XLSX.utils.book_append_sheet(wb, ws1, '产品统计')

        // 2. Sheet 2: 出库明细
        const sheet2Headers = ['出库类型', '产品', '件数', '客户']
        if (isGift) {
          sheet2Headers.push('客户身份')
        }
        sheet2Headers.push('开发人', '客户负责人', '生产批次', '经办人', '库管', '状态')
        if (isTrial) {
          sheet2Headers.push('是否消费', '消费时间')
        }
        sheet2Headers.push('出库时间')
        const sheet2Data = [sheet2Headers]

        this.filteredRecords.forEach(r => {
          const rowData = [
            this.formatRecordType(r),
            r.productNames || '',
            r.totalQuantity != null ? Number(r.totalQuantity) : '',
            r.customerName || ''
          ]
          if (isGift) {
            rowData.push(r.customerIdentity || this.resolveCustomerIdentity(r) || '')
          }
          rowData.push(
            r.developer || '',
            r.customerManager || '',
            (r.batchNos || '').toString().replace(/\n/g, ' '),
            r.operator || '',
            r.warehouseKeeper || '',
            this.statusText(r.status)
          )
          if (isTrial) {
            rowData.push(
              r.consumptionStatus === 1 ? '已消费' : '未消费',
              (r.consumptionTime || '').toString().slice(0, 10)
            )
          }
          rowData.push([0, 2, 6].includes(Number(r.status)) ? '' : (r.finishTime || '').toString().slice(0, 10))
          sheet2Data.push(rowData)
        })
        const ws2 = XLSX.utils.aoa_to_sheet(sheet2Data)
        const sheet2Cols = [
          { wch: 14 },
          { wch: 22 },
          { wch: 10 },
          { wch: 16 }
        ]
        if (isGift) {
          sheet2Cols.push({ wch: 14 })
        }
        sheet2Cols.push(
          { wch: 14 },
          { wch: 14 },
          { wch: 16 },
          { wch: 14 },
          { wch: 14 },
          { wch: 12 }
        )
        if (isTrial) {
          sheet2Cols.push({ wch: 12 }, { wch: 14 })
        }
        sheet2Cols.push({ wch: 14 })
        ws2['!cols'] = sheet2Cols
        XLSX.utils.book_append_sheet(wb, ws2, '出库明细')

        // 3. Sheet 3: 产品试用记录 (仅试用模式下有数据时添加)
        if (isTrial && this.filteredTrialRecords.length > 0) {
          const sheet3Headers = ['客户', '产品', '总件数', '已审核件数', '待审核件数', '试用类型', '申请次数', '限额次数', '已审核次数', '待审核次数', '业务员', '消费情况', '消费时间']
          const sheet3Data = [sheet3Headers]
          this.filteredTrialRecords.forEach(r => {
            sheet3Data.push([
              r.customerName || '',
              r.productName || '',
              r.totalQuantity !== undefined ? r.totalQuantity : (r.quantity || 0),
              r.approvedQuantity !== undefined ? r.approvedQuantity : (r.quantity || 0),
              r.pendingQuantity || 0,
              r.applicableTypeName || '',
              r.totalApplyCount !== undefined ? r.totalApplyCount : ((r.approvedCount || 0) + (r.pendingCount || 0)),
              r.applicableCount || '不限',
              r.approvedCount || 0,
              r.pendingCount || 0,
              r.salesman || '',
              r.consumptionStatus === 1 ? '已消费' : '未消费',
              (r.consumptionTime || '').toString().slice(0, 10)
            ])
          })
          const ws3 = XLSX.utils.aoa_to_sheet(sheet3Data)
          ws3['!cols'] = [
            { wch: 14 }, { wch: 22 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 }
          ]
          XLSX.utils.book_append_sheet(wb, ws3, '产品试用记录')
        }

        const tags = []
        if (this.customerFilter && this.customerFilter.trim()) tags.push(`客户_${this.customerFilter.trim()}`)
        if (this.developerFilter && this.developerFilter.trim()) tags.push(`开发人_${this.developerFilter.trim()}`)
        if (this.managerFilter && this.managerFilter.trim()) tags.push(`客户负责人_${this.managerFilter.trim()}`)
        if (this.productFilter && this.productFilter.trim()) tags.push(`产品_${this.productFilter.trim()}`)
        if (this.currentType === '礼品' && this.giftTypeFilter) {
          const opt = (this.giftTypeOptions || []).find(t => t.id === this.giftTypeFilter)
          if (opt) tags.push(`礼品类型_${opt.name}`)
        }
        if (this.currentType === '礼品' && this.customerIdentityFilter) {
          tags.push(`客户身份_${this.customerIdentityFilter.trim()}`)
        }
        if (isTrial && this.trialTypeFilter) {
          const opt = (this.applicableTypeOptions || []).find(t => t.id === this.trialTypeFilter)
          if (opt) tags.push(`试用类型_${opt.name}`)
        }
        if (isTrial && this.consumptionStatusFilter !== '' && this.consumptionStatusFilter != null) {
          tags.push(this.consumptionStatusFilter === 1 ? '已消费' : '未消费')
        }
        if (this.currentType === '送存货' && this.stockTypeFilter) {
          tags.push(`存货类型_${this.stockTypeFilter}`)
        }
        const suffix = tags.length ? `_${tags.join('_')}` : ''
        const filename = `报表_${this.currentType}_${this.rangeLabel || '全部'}${suffix}.xlsx`

        XLSX.writeFile(wb, filename)
        this.$message.success('已导出报表')
      } catch (err) {
        console.error('Export excel error:', err)
        this.$message.error('导出失败，请重试')
      }
    },
    // 导出当前周期产品统计表为 Excel
    exportProductStats () {
      if (!this.shownItems.length) {
        this.$message.warning('当前没有可导出的统计数据')
        return
      }
      try {
        const wb = XLSX.utils.book_new()
        const sheetData = [
          ['序号', '出库类型', '统计周期', '产品名称', '数量（件）']
        ]
        this.shownItems.forEach((item, idx) => {
          sheetData.push([
            idx + 1,
            this.currentType || '',
            this.rangeLabel || '全部',
            item.productName || '',
            Number(item.quantity) || 0
          ])
        })
        const totalSum = this.shownItems.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0)
        sheetData.push(['合计', this.currentType || '', this.rangeLabel || '全部', '全部产品', totalSum])
        const ws = XLSX.utils.aoa_to_sheet(sheetData)
        ws['!cols'] = [{ wch: 8 }, { wch: 14 }, { wch: 26 }, { wch: 24 }, { wch: 14 }]
        XLSX.utils.book_append_sheet(wb, ws, '产品统计')

        const pTags = []
        if (this.productFilter) pTags.push(`产品_${this.productFilter.trim()}`)
        if (this.currentType === '礼品' && this.giftTypeFilter) {
          const opt = (this.giftTypeOptions || []).find(t => t.id === this.giftTypeFilter)
          if (opt) pTags.push(`礼品类型_${opt.name}`)
        }
        const pSuffix = pTags.length ? `_${pTags.join('_')}` : ''
        const filename = `产品统计_${this.currentType}_${this.rangeLabel || '全部'}${pSuffix}.xlsx`
        XLSX.writeFile(wb, filename)
      } catch (e) {
        console.error(e)
        this.$message.error('导出失败')
      }
    },
    // 导出产品试用记录为 Excel
    exportTrialRecords () {
      if (!this.filteredTrialRecords.length) {
        this.$message.warning('当前没有可导出的试用记录')
        return
      }
      try {
        const wb = XLSX.utils.book_new()
        const sheetHeaders = ['客户', '产品', '总件数', '已审核件数', '待审核件数', '试用类型', '申请次数', '限额次数', '已审核次数', '待审核次数', '业务员', '消费情况', '消费时间']
        const sheetData = [sheetHeaders]

        this.filteredTrialRecords.forEach(r => {
          sheetData.push([
            r.customerName || '',
            r.productName || '',
            r.totalQuantity !== undefined ? r.totalQuantity : (r.quantity || 0),
            r.approvedQuantity !== undefined ? r.approvedQuantity : (r.quantity || 0),
            r.pendingQuantity || 0,
            r.applicableTypeName || '',
            r.totalApplyCount !== undefined ? r.totalApplyCount : ((r.approvedCount || 0) + (r.pendingCount || 0)),
            r.applicableCount || '不限',
            r.approvedCount || 0,
            r.pendingCount || 0,
            r.salesman || '',
            r.consumptionStatus === 1 ? '已消费' : '未消费',
            (r.consumptionTime || '').toString().slice(0, 10)
          ])
        })
        const ws = XLSX.utils.aoa_to_sheet(sheetData)
        ws['!cols'] = [
          { wch: 14 }, { wch: 22 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 }
        ]
        XLSX.utils.book_append_sheet(wb, ws, '产品试用记录')

        const tags = []
        if (this.customerFilter && this.customerFilter.trim()) tags.push(`客户_${this.customerFilter.trim()}`)
        if (this.developerFilter && this.developerFilter.trim()) tags.push(`开发人_${this.developerFilter.trim()}`)
        if (this.managerFilter && this.managerFilter.trim()) tags.push(`客户负责人_${this.managerFilter.trim()}`)
        if (this.productFilter && this.productFilter.trim()) tags.push(`产品_${this.productFilter.trim()}`)
        if (this.trialTypeFilter) {
          const opt = (this.applicableTypeOptions || []).find(t => t.id === this.trialTypeFilter)
          if (opt) tags.push(`试用类型_${opt.name}`)
        }
        if (this.consumptionStatusFilter !== '' && this.consumptionStatusFilter != null) {
          tags.push(this.consumptionStatusFilter === 1 ? '已消费' : '未消费')
        }
        const suffix = tags.length ? `_${tags.join('_')}` : ''
        const filename = `产品试用记录_${this.rangeLabel || '全部'}${suffix}.xlsx`
        XLSX.writeFile(wb, filename)
        this.$message.success('已导出产品试用记录')
      } catch (e) {
        console.error(e)
        this.$message.error('导出失败')
      }
    },
    // 导出当前（类型 + 周期 + 筛选后）的出库记录明细为 Excel
    exportRecords () {
      if (!this.filteredRecords.length) {
        this.$message.warning('当前没有可导出的记录')
        return
      }
      try {
        const wb = XLSX.utils.book_new()
        const isTrial = this.currentType === '试用'
        const isGift = this.currentType === '礼品'
        const sheetHeaders = ['出库类型', '产品', '件数', '客户']
        if (isGift) {
          sheetHeaders.push('客户身份')
        }
        sheetHeaders.push('开发人', '客户负责人', '生产批次', '经办人', '库管', '状态')
        if (isTrial) {
          sheetHeaders.push('是否消费', '消费时间')
        }
        sheetHeaders.push('出库时间')
        const sheetData = [sheetHeaders]

        this.filteredRecords.forEach(r => {
          const rowData = [
            this.formatRecordType(r),
            r.productNames || '',
            r.totalQuantity != null ? Number(r.totalQuantity) : '',
            r.customerName || ''
          ]
          if (isGift) {
            rowData.push(r.customerIdentity || this.resolveCustomerIdentity(r) || '')
          }
          rowData.push(
            r.developer || '',
            r.customerManager || '',
            (r.batchNos || '').toString().replace(/\n/g, ' '),
            r.operator || '',
            r.warehouseKeeper || '',
            this.statusText(r.status)
          )
          if (isTrial) {
            rowData.push(
              r.consumptionStatus === 1 ? '已消费' : '未消费',
              (r.consumptionTime || '').toString().slice(0, 10)
            )
          }
          rowData.push([0, 2, 6].includes(Number(r.status)) ? '' : (r.finishTime || '').toString().slice(0, 10))
          sheetData.push(rowData)
        })
        const ws = XLSX.utils.aoa_to_sheet(sheetData)
        const sheetCols = [
          { wch: 14 }, { wch: 22 }, { wch: 10 }, { wch: 16 }
        ]
        if (isGift) {
          sheetCols.push({ wch: 14 })
        }
        sheetCols.push(
          { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }
        )
        if (isTrial) {
          sheetCols.push({ wch: 12 }, { wch: 14 })
        }
        sheetCols.push({ wch: 14 })
        ws['!cols'] = sheetCols
        XLSX.utils.book_append_sheet(wb, ws, '出库明细')

        const tags = []
        if (this.customerFilter && this.customerFilter.trim()) tags.push(`客户_${this.customerFilter.trim()}`)
        if (this.developerFilter && this.developerFilter.trim()) tags.push(`开发人_${this.developerFilter.trim()}`)
        if (this.managerFilter && this.managerFilter.trim()) tags.push(`客户负责人_${this.managerFilter.trim()}`)
        if (this.productFilter && this.productFilter.trim()) tags.push(`产品_${this.productFilter.trim()}`)
        if (this.currentType === '礼品' && this.giftTypeFilter) {
          const opt = (this.giftTypeOptions || []).find(t => t.id === this.giftTypeFilter)
          if (opt) tags.push(`礼品类型_${opt.name}`)
        }
        if (this.currentType === '礼品' && this.customerIdentityFilter) {
          tags.push(`客户身份_${this.customerIdentityFilter.trim()}`)
        }
        if (isTrial && this.trialTypeFilter) {
          const opt = (this.applicableTypeOptions || []).find(t => t.id === this.trialTypeFilter)
          if (opt) tags.push(`试用类型_${opt.name}`)
        }
        if (isTrial && this.consumptionStatusFilter !== '' && this.consumptionStatusFilter != null) {
          tags.push(this.consumptionStatusFilter === 1 ? '已消费' : '未消费')
        }
        if (this.currentType === '送存货' && this.stockTypeFilter) {
          tags.push(`存货类型_${this.stockTypeFilter}`)
        }
        const suffix = tags.length ? `_${tags.join('_')}` : ''
        const filename = `出库明细_${this.currentType}_${this.rangeLabel || '全部'}${suffix}.xlsx`
        XLSX.writeFile(wb, filename)
      } catch (e) {
        console.error(e)
        this.$message.error('导出失败')
      }
    }
  }
}
</script>

<style scoped>
.report-page { padding: 4px; }
.mb-4 { margin-bottom: 16px; }
.bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.bar-label { font-size: 13px; color: #606266; font-weight: 600; flex-shrink: 0; }
.muted { color: #909399; font-size: 13px; }
.empty { text-align: center; padding: 18px 0; }

.stat-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; }
.stat-card { border-radius: 8px; padding: 20px 16px; color: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.stat-card.today { background: linear-gradient(135deg,#36d1dc,#5b86e5); }
.stat-card.month { background: linear-gradient(135deg,#11998e,#38ef7d); }
.stat-card.quarter { background: linear-gradient(135deg,#00c6ff,#0072ff); }
.stat-card.year  { background: linear-gradient(135deg,#f7971e,#ffd200); }
.stat-card.all   { background: linear-gradient(135deg,#8e2de2,#4a00e0); }
.stat-num { font-size: 30px; font-weight: 700; line-height: 1.2; }
.stat-label { font-size: 13px; opacity: 0.92; margin-top: 6px; }

.card-head { display: flex; justify-content: space-between; align-items: center; font-size: 14px; font-weight: 600; color: #303133; }
.head-tools { display: flex; align-items: center; gap: 8px; }
.card-head i { color: #409eff; margin-right: 4px; }
.head-sum { font-size: 13px; color: #409eff; font-weight: 600; }
</style>
